import { type TargetedEvent } from 'preact/compat'
import { navigate } from 'wouter-preact/use-browser-location'
import { createState } from 'shared'
import { DAppService, ErrorType, ForManagingDApps, GuardInput } from 'core'

import {
	addDAppToProjectDetail,
	application,
	emitSystemNotification,
	projectDetailsState,
	resetSystemNotification
} from 'state'

export interface Template {
	id: string
	name: string
	subtitle: string
	description: string
}

const dAppNameStateContainer = createState('')
const dAppDescriptionStateContainer = createState('')
const selectedTemplateStateContainer = createState('')
const formErrorsStateContainer = createState<
	Record<string, string | undefined>
>({})
const isDAppLoadingStateContainer = createState(false)

export const dAppNameState = dAppNameStateContainer.state
export const dAppDescriptionState = dAppDescriptionStateContainer.state
export const selectedTemplateState = selectedTemplateStateContainer.state
export const formErrors = formErrorsStateContainer.state
export const isDAppLoading = isDAppLoadingStateContainer.state

const updateFormErrors = (field: string, error?: string) => {
	formErrorsStateContainer.updateState(prevErrorState => {
		if (prevErrorState[field] === error) return prevErrorState
		return {
			...prevErrorState,
			[field]: error
		}
	})
}

dAppNameStateContainer.onUpdateState(value => {
	const dapps = projectDetailsState.peek()?.dapps || []
	const error = DAppService.ValidateDAppName(value, dapps)

	if (error) {
		updateFormErrors('name', error.inputErrors?.name)
	} else if (formErrors.peek().name !== '') {
		updateFormErrors('name', '')
	}
})

dAppDescriptionStateContainer.onUpdateState(value => {
	const error = GuardInput.check(
		ForManagingDApps.CreateDAppBasePayload.partial(),
		{
			description: value
		}
	)

	if (error) {
		updateFormErrors('description', error.inputErrors?.description)
	} else if (formErrors.peek().description !== '') {
		updateFormErrors('description', '')
	}
})

export const setDAppName = (value: string) => {
	dAppNameStateContainer.updateState(() => value)
}

export const setDAppDescription = (value: string) => {
	dAppDescriptionStateContainer.updateState(() => value)
}

export const setSelectedTemplate = (id: string) => {
	selectedTemplateStateContainer.updateState(() => id)
}

export const resetDAppForm = () => {
	dAppNameStateContainer.updateState(() => '')
	dAppDescriptionStateContainer.updateState(() => '')
	selectedTemplateStateContainer.updateState(() => '')
	formErrorsStateContainer.updateState(() => ({}))
}

export const getTemplates = async (): Promise<Template[]> => {
	const { data, error } = await application.value
		.forManagingDApps()
		.getTemplates()

	if (error) {
		emitSystemNotification({
			error,
			display: 'dialog'
		})

		return []
	}

	return data!.map(e => ({
		id: e.id,
		name: e.name,
		subtitle: e.subtitle,
		description: e.description
	}))
}

export const createDAppFromTemplate = async (
	event: TargetedEvent<HTMLFormElement, Event>
): Promise<void> => {
	event.preventDefault()
	const projectId = projectDetailsState.peek()!.publicId!
	isDAppLoadingStateContainer.updateState(() => true)
	resetSystemNotification()

	const { data, error } = await application.value
		.forManagingDApps()
		.createDAppFromTemplate({
			name: dAppNameState.peek()!,
			description: dAppDescriptionState.peek()!,
			templateId: selectedTemplateState.peek()!,
			publicProjectId: projectId
		})

	isDAppLoadingStateContainer.updateState(() => false)

	if (error) {
		if (error.type === ErrorType.InvalidInput) {
			// If there is an error with the input, in this instance, the templateId
			// is the only input that can be invalid because the name and description
			// were validated before the request was made
			error.message = error.inputErrors!.templateId!

			if (error.inputErrors!.templateId) {
				emitSystemNotification({
					display: 'dialog',
					error
				})
			}

			return
		}

		emitSystemNotification({ error, display: 'dialog' })
		return
	}

	addDAppToProjectDetail(projectId, data!)

	navigate(`/dapp/${projectId}/smart-contract/${data!.name}/workspace`)

	resetDAppForm()
}
